import './AddDataSourceModal.scss';
import { useCallback, useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Button } from '@Components/Button';
import { Appearance, Design } from '@Components/designConstants';
import { SegmentedControlButton } from '@Components/SegmentedControlButton';
import { SourceFilePicker } from './SourceFilePicker';

// Uploading is the only source in v1; linking a Google Drive document joins this list when
// it ships.
const SourceTab = {
  UPLOAD: 'Upload file'
} as const;
type SourceTabType = (typeof SourceTab)[keyof typeof SourceTab];

interface AddDataSourceModalProps {
  show: boolean;
  onHide: () => void;
  onSubmit: (file: File) => void | Promise<void>;
  isSubmitting?: boolean;
}

export const AddDataSourceModal = ({
  show,
  onHide,
  onSubmit,
  isSubmitting = false
}: AddDataSourceModalProps) => {
  const [activeTab, setActiveTab] = useState<SourceTabType>(SourceTab.UPLOAD);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setSelectedFile(null);
    setError(null);
    setActiveTab(SourceTab.UPLOAD);
  }, []);

  // Also reset when a consumer hides the modal by flipping `show` rather than calling
  // onHide. Without this, reopening would still show the previous file.
  useEffect(() => {
    if (!show) {
      reset();
    }
  }, [show, reset]);

  const close = () => {
    reset();
    onHide();
  };

  // Submit failures render in the validation-error slot, and the modal stays open so the
  // file can be retried.
  const submit = async () => {
    if (!selectedFile) return;
    setError(null);
    try {
      await onSubmit(selectedFile);
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <Modal show={show} onHide={close} className='add-data-source-modal' centered>
      <Modal.Header closeButton>
        <Modal.Title>Add a data source to this section</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <SegmentedControlButton
          options={[SourceTab.UPLOAD]}
          selectedOption={activeTab}
          onChange={setActiveTab}
        />

        <SourceFilePicker
          selectedFile={selectedFile}
          onFile={file => {
            setError(null);
            setSelectedFile(file);
          }}
          onError={message => {
            setSelectedFile(null);
            setError(message);
          }}
        />

        {error && <div className='validation-error'>{error}</div>}
      </Modal.Body>

      <Modal.Footer className='onboarding-modal-footer'>
        <Button
          appearance={Appearance.OUTLINED}
          design={Design.NEUTRAL}
          disabled={isSubmitting}
          onClick={close}
        >
          Cancel
        </Button>
        <Button disabled={!selectedFile} loading={isSubmitting} onClick={submit}>
          Add source
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
